import React from "react";
import { CheckCircle } from "lucide-react";

const servicesData = [
  {
    id: 1,
    title: "Website Design & Development",
    description:
      "We build pixel-perfect, lightning-fast websites designed to convert. Our focus is on creating responsive layouts that look stunning on any device.",
    features: [
      "Responsive Layouts",
      "Fast Loading Speeds",
      "SEO Optimized Structure",
    ],
    image:
      "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=2072&auto=format&fit=crop",
    stat: "100%",
    statText: "Responsive",
  },
  {
    id: 2,
    title: "UI / UX Design",
    description:
      "User experience is at the heart of what we do. We create intuitive interfaces and user flows that make navigating your digital products a breeze.",
    features: [
      "User Journey Mapping",
      "High-Fidelity Prototyping",
      "Modern Aesthetic",
    ],
    image:
      "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?q=80&w=2070&auto=format&fit=crop",
    stat: "5x",
    statText: "Engagement",
  },
  {
    id: 3,
    title: "Digital Solutions",
    description:
      "Stop worrying about the technical details. We provide end-to-end infrastructure solutions to keep your business online and secure.",
    features: [
      "Domain Registration",
      "Secure Hosting",
      "Professional Email Setup",
    ],
    image:
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070&auto=format&fit=crop",
    stat: "24/7",
    statText: "Uptime",
  },
  {
    id: 4,
    title: "Client Support & Maintenance",
    description:
      "We don't just build and leave. Our dedicated support team is here to ensure your digital assets are always running smoothly and updated.",
    features: ["Priority Bug Fixes", "Regular Updates", "Technical Assistance"],
    image:
      "https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=2069&auto=format&fit=crop",
    stat: "1hr",
    statText: "Response",
  },
  {
    id: 5,
    title: "Social Media Packages",
    description:
      "Full-service social media management. We handle the production, editing, and day-to-day management of your accounts so you can focus on business.",
    features: ["Professional Shoots", "Video Editing", "Account Handling"],
    image:
      "https://images.unsplash.com/photo-1611162616475-46b635cb6868?q=80&w=1974&auto=format&fit=crop",
    stat: "30+",
    statText: "Clients",
  },
  {
    id: 6,
    title: "Content Creation",
    description:
      "Dominate the feed with high-quality content. From viral reels to insightful long-form posts, we craft the narrative your audience wants to see.",
    features: ["Reels & Shorts", "Long-form Content", "Instagram Post Design"],
    image:
      "https://images.unsplash.com/photo-1533750349088-cd871a92f312?q=80&w=2070&auto=format&fit=crop",
    stat: "4k",
    statText: "Quality",
  },
  {
    id: 7,
    title: "Influencer Marketing",
    description:
      "Leverage the power of voices that matter. We connect your brand with influencers who align with your values to drive authentic reach.",
    features: [
      "Influencer Outreach",
      "Campaign Strategy",
      "Performance Tracking",
    ],
    image:
      "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=2070&auto=format&fit=crop",
    stat: "10x",
    statText: "ROI",
  },
];

const CoreServices = () => {
  return (
    <section className="mt-24 overflow-hidden">
      {/* Header Section - Pure White */}
      <div className="bg-white pt-10 pb-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-left mb-0 max-w-3xl">
            <span className="inline-block py-1 px-3 rounded-full bg-blue-100 text-blue-600 text-sm font-semibold mb-4">
              Our Expertise
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Core Services
            </h2>
            <p className="text-gray-600 text-lg">
              We deliver high-quality digital services designed to transform
              your business presence.
            </p>
          </div>
        </div>
      </div>

      {/* Services Loop with Alternating Backgrounds */}
      <div className="flex flex-col">
        {servicesData.map((service, index) => {
          const isEven = index % 2 === 0;
          const bgColor = isEven ? "bg-white" : "bg-gray-100";

          return (
            <div
              key={service.id}
              id={`service-${service.id}`}
              className={`${bgColor} py-16`}
            >
              <div className="container mx-auto px-4 md:px-6">
                <div
                  className={`flex flex-col ${
                    isEven ? "lg:flex-row-reverse" : "lg:flex-row"
                  } items-center gap-12 lg:gap-20`}
                >
                  {/* Image Section */}
                  <div className="w-full lg:w-1/2 relative group">
                    <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute bottom-2 left-2 bg-blue-500 text-white p-4 rounded-xl shadow-lg z-10">
                        <p className="text-2xl font-bold">{service.stat}</p>
                        <p className="text-xs uppercase tracking-wider opacity-90">
                          {service.statText}
                        </p>
                      </div>
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500"></div>
                    </div>
                    {/* Decorative background element */}
                    <div
                      className={`absolute -z-10 top-8 w-full h-full bg-blue-100 rounded-2xl hidden lg:block ${
                        isEven ? "-left-8" : "-right-8"
                      }`}
                    ></div>
                  </div>

                  {/* Content Section */}
                  <div className="w-full lg:w-1/2">
                    <h3 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                      {service.description}
                    </p>

                    <ul className="space-y-4 mb-8">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3">
                          <CheckCircle className="w-6 h-6 text-blue-500 flex-shrink-0" />
                          <span className="text-gray-700 font-medium">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default CoreServices;
